import React from "react";
import { Box, Typography, alpha } from "@mui/material";
import { NAVY, CRIMSON, GOLD } from "../theme/theme";

interface PageHeaderProps {
  title: string;
  icon?: React.ReactNode;
  subtitle?: string;
  action?: React.ReactNode;
}

// Compact gradient banner reused at the top of every main app page —
// mirrors the Landing page's navy → crimson hero so the logged-in app
// feels like one continuous brand, not a plain admin panel bolted on.
export default function PageHeader({ title, icon, subtitle, action }: PageHeaderProps) {
  return (
    <Box
      sx={{
        position: "relative",
        background: `linear-gradient(135deg, ${NAVY} 0%, #1a3a7a 55%, ${alpha(CRIMSON, 0.92)} 100%)`,
        color: "white",
        px: 2,
        py: 1.1,
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        "&::before": {
          content: '""',
          position: "absolute",
          top: -40, right: -30,
          width: 120, height: 120,
          borderRadius: "50%",
          background: alpha(GOLD, 0.16),
          filter: "blur(32px)",
          pointerEvents: "none",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: -45, left: -30,
          width: 110, height: 110,
          borderRadius: "50%",
          background: alpha(CRIMSON, 0.22),
          filter: "blur(32px)",
          pointerEvents: "none",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.1, position: "relative", zIndex: 1, minWidth: 0 }}>
        {icon && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 30,
              height: 30,
              borderRadius: "9px",
              bgcolor: alpha("#fff", 0.15),
              flexShrink: 0,
              "& svg": { fontSize: "1.1rem !important" },
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ minWidth: 0, display: "flex", alignItems: "baseline", gap: 0.8 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }} noWrap>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ opacity: 0.8 }} noWrap>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {action && (
        <Box sx={{ position: "relative", zIndex: 1, flexShrink: 0 }}>
          {action}
        </Box>
      )}
    </Box>
  );
}
